import { EditPageTemplate } from "../../EditPageTemplate";
import { mt } from "../../../../utils/tracking";
import { useEffect, useState } from "react";
import { UpdateStatus } from "../../SettingsPage.types";

import { NotFound } from "../../../../not-found";
import { useScrollToTop } from "../../../../hooks/useScrollToTop";
import {
  useEmail,
  useIsStateFarmPartitioned,
} from "../../../../app/Providers/AppContext";
import {
  DocumentDeliverySelection,
  DocumentDeliverySelectionState,
  DocumentDeliveryValue,
} from "./components/DocumentDeliverySelection";
import { navigateToSettings } from "../../utils";
import { PaperlessTermsModal } from "./components/PaperlessTermsModal";
import { saveDocumentDeliveryInfo } from "../../utils/saveDocumentDeliveryInfo";
import { usePolicies } from "../../../Insurance/usePolicies";

const PAPERLESS_TERMS_MODAL_ID = "paperless-terms-modal";

export default function DocumentDeliveryPage() {
  const isStateFarmPartitioned = useIsStateFarmPartitioned();

  if (isStateFarmPartitioned) {
    return <NotFound />;
  }

  const policies = usePolicies();
  const hasUsCorePolicy = policies?.some(
    (p) => p.source === "CORE" && p.country === "USA"
  );

  if (!hasUsCorePolicy) {
    return <NotFound />;
  }

  useScrollToTop();

  // Paperless details are the same for all policies since it's at the customer level
  const paperlessDetails = policies[0].paperlessDetails;
  const defaultEmail = useEmail();

  return (
    <DocumentDeliveryPageWrapped
      currentDeliveryMethod={
        paperlessDetails.status === "ACTIVE" ? "paperless" : "mail"
      }
      paperlessEmail={paperlessDetails.email ?? defaultEmail}
    />
  );
}

type DocumentDeliveryPageWrappedProps = {
  /** The user's current document delivery preference. */
  currentDeliveryMethod: DocumentDeliveryValue;
  /** The user's current paperless email. */
  paperlessEmail?: string;
};

const DocumentDeliveryPageWrapped = ({
  currentDeliveryMethod,
  paperlessEmail,
}: DocumentDeliveryPageWrappedProps) => {
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>("ok");
  const [state, setState] = useState<
    DocumentDeliverySelectionState | undefined
  >();

  useEffect(() => {
    mt.trackView();
  }, []);

  return (
    <>
      <EditPageTemplate
        heading="Document delivery"
        message="Control how you receive your billing alerts and policy statements.
          Embrace the efficiency of paperless communication or continue
          receiving paper documents in your mailbox."
        unsavedChangesDialogHeading="Save document delivery changes?"
        pending={updateStatus == "pending"}
        isFormDirty={
          !!state &&
          (state.selectedOption !== currentDeliveryMethod ||
            (state.emailChanged && state.email !== paperlessEmail))
        }
        hasValidationErrors={
          state?.selectedOption === "paperless" && !state?.emailValid
        }
        showMutateError={updateStatus === "mutateError"}
        onClickSave={onClickSave}
      >
        <DocumentDeliverySelection
          initialOption={currentDeliveryMethod}
          defaultEmail={paperlessEmail}
          termsModalId={PAPERLESS_TERMS_MODAL_ID}
          onChange={setState}
        />
      </EditPageTemplate>
      <PaperlessTermsModal id={PAPERLESS_TERMS_MODAL_ID} />
    </>
  );

  async function onClickSave(): Promise<void> {
    if (state) {
      mt.trackAction({ actionType: "button", actionName: "save" });
      setUpdateStatus("pending");

      try {
        await saveDocumentDeliveryInfo({
          email: state.emailChanged ? state.email : undefined,
          status: state.selectedOption === "paperless" ? "ACTIVE" : "INACTIVE",
        });
      } catch {
        setUpdateStatus("mutateError");
        return;
      }

      navigateToSettings(true);
    }
  }
};
